<template>
  <v-container fluid>
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <div>
              <h2 class="text-h4 font-weight-bold">RAPS Records</h2>
              <p class="text-subtitle-1 text--secondary mb-0">
                {{ fileInfo.originalFileName }} - {{ fileInfo.fileId }}
              </p>
            </div>
            <v-btn
              color="primary"
              @click="$router.go(-1)"
            >
              <v-icon left>mdi-arrow-left</v-icon>
              Back
            </v-btn>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtros -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="filters.status"
                  :items="statusOptions"
                  label="Status"
                  clearable
                  @change="loadRecords"
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="filters.memberId"
                  label="Search by Member ID"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  @input="debounceSearch"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="filters.icd10Code"
                  label="Search by ICD-10 Code"
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  @input="debounceSearch"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3" class="d-flex align-center">
                <v-btn
                  color="primary"
                  @click="loadRecords"
                  class="mr-2"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Refresh
                </v-btn>
                <v-btn
                  color="secondary"
                  @click="clearFilters"
                >
                  <v-icon left>mdi-filter-remove</v-icon>
                  Clear
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabela de Registros -->
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <span class="text-h6">RAPS Records</span>
            <v-spacer></v-spacer>
            <v-chip
              v-if="records.length > 0"
              color="primary"
              text-color="white"
            >
              {{ pagination.totalRecords }} record(s)
            </v-chip>
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="records"
            :loading="loading"
            :items-per-page="pagination.limit"
            :page="pagination.currentPage"
            :server-items-length="pagination.totalRecords"
            @update:page="onPageChange"
            class="elevation-1"
          >
            <!-- Status -->
            <template v-slot:item.status="{ item }">
              <v-chip
                :color="getStatusColor(item.status)"
                text-color="white"
                small
              >
                {{ getStatusText(item.status) }}
              </v-chip>
            </template>

            <!-- Member ID -->
            <template v-slot:item.memberId="{ item }">
              <div>
                <div class="font-weight-bold">{{ item.memberId }}</div>
                <div class="text-caption text--secondary" v-if="item.medicareNumber">
                  Medicare: {{ item.medicareNumber }}
                </div>
              </div>
            </template>

            <!-- Data de Nascimento -->
            <template v-slot:item.patientDOB="{ item }">
              {{ formatDate(item.patientDOB) }}
            </template>

            <!-- Data de Ocorrência -->
            <template v-slot:item.occurrenceDateTime="{ item }">
              {{ formatDate(item.occurrenceDateTime) }}
            </template>

            <!-- Erro -->
            <template v-slot:item.errorMessage="{ item }">
              <div v-if="item.errorMessage">
                <v-chip
                  color="error"
                  text-color="white"
                  small
                  @click="showErrorDetails(item)"
                >
                  <v-icon left small>mdi-alert</v-icon>
                  View Error
                </v-chip>
              </div>
              <span v-else class="text--secondary">-</span>
            </template>

            <!-- Ações -->
            <template v-slot:item.actions="{ item }">
              <v-menu bottom left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="viewRecordDetails(item)">
                    <v-list-item-icon>
                      <v-icon>mdi-eye</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>View Details</v-list-item-title>
                  </v-list-item>
                  
                  <v-list-item 
                    @click="correctRecord(item)"
                    :disabled="item.status !== 'error'"
                  >
                    <v-list-item-icon>
                      <v-icon>mdi-pencil</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Correct</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog de Detalhes do Registro -->
    <v-dialog v-model="showRecordDetailsDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Record Details</span>
        </v-card-title>
        <v-card-text>
          <v-row v-if="selectedRecord">
            <v-col cols="12" sm="6">
              <v-text-field
                label="Member ID"
                :value="selectedRecord.memberId"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Medicare Number"
                :value="selectedRecord.medicareNumber || 'N/A'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Patient Control No"
                :value="selectedRecord.patientControlNo || 'N/A'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Date of Birth"
                :value="formatDate(selectedRecord.patientDOB)"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="ICD-10 Code"
                :value="selectedRecord.icd10Code"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Occurrence Date"
                :value="formatDate(selectedRecord.occurrenceDateTime)"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Status"
                :value="getStatusText(selectedRecord.status)"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Error Code"
                :value="selectedRecord.errorCode || 'N/A'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" v-if="selectedRecord.errorMessage">
              <v-textarea
                label="Error Message"
                :value="selectedRecord.errorMessage"
                readonly
                outlined
                rows="3"
              ></v-textarea>
            </v-col>
            <v-col cols="12" v-if="selectedRecord.errorDetails">
              <v-textarea
                label="Error Details"
                :value="selectedRecord.errorDetails"
                readonly
                outlined
                rows="3"
              ></v-textarea>
            </v-col>
            <v-col cols="12" v-if="selectedRecord.correctedData">
              <v-card color="success" dark>
                <v-card-title>
                  <v-icon left>mdi-check-circle</v-icon>
                  Corrected Data
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6" v-if="selectedRecord.correctedData.memberId">
                      <strong>Member ID:</strong> {{ selectedRecord.correctedData.memberId }}
                    </v-col>
                    <v-col cols="12" sm="6" v-if="selectedRecord.correctedData.medicareNumber">
                      <strong>Medicare Number:</strong> {{ selectedRecord.correctedData.medicareNumber }}
                    </v-col>
                    <v-col cols="12" sm="6" v-if="selectedRecord.correctedData.icd10Code">
                      <strong>ICD-10 Code:</strong> {{ selectedRecord.correctedData.icd10Code }}
                    </v-col>
                    <v-col cols="12" sm="6" v-if="selectedRecord.correctedData.patientDOB">
                      <strong>Date of Birth:</strong> {{ formatDate(selectedRecord.correctedData.patientDOB) }}
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="showRecordDetailsDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Correção -->
    <v-dialog v-model="showCorrectionDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h5">Correct Record</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="correctionForm" v-model="correctionFormValid">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="correctionData.memberId"
                  label="Member ID"
                  :rules="[v => !!v || 'Member ID is required']"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="correctionData.medicareNumber"
                  label="Medicare Number"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="correctionData.patientControlNo"
                  label="Patient Control No"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="correctionData.patientDOB"
                  label="Date of Birth"
                  type="date"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="correctionData.icd10Code"
                  label="ICD-10 Code"
                  :rules="[v => !!v || 'ICD-10 Code is required']"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="correctionData.occurrenceDateTime"
                  label="Occurrence Date"
                  type="date"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="closeCorrectionDialog"
            :disabled="correcting"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="saveCorrection"
            :loading="correcting"
            :disabled="!correctionFormValid"
          >
            <v-icon left>mdi-check</v-icon>
            Save Correction
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Detalhes do Erro -->
    <v-dialog v-model="showErrorDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Error Details</span>
        </v-card-title>
        <v-card-text>
          <v-alert
            type="error"
            text
            class="mb-3"
          >
            <strong>Code:</strong> {{ errorDetails.errorCode || 'N/A' }}
          </v-alert>
          <v-textarea
            label="Error Message"
            :value="errorDetails.errorMessage"
            readonly
            outlined
            rows="3"
          ></v-textarea>
          <v-textarea
            v-if="errorDetails.errorDetails"
            label="Additional Details"
            :value="errorDetails.errorDetails"
            readonly
            outlined
            rows="3"
            class="mt-3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="showErrorDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificações -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from 'axios';

export default {
  name: 'RAPSRecords',
  data() {
    return {
      loading: false,
      correcting: false,
      fileInfo: {},
      records: [],
      selectedRecord: null,
      showRecordDetailsDialog: false,
      showCorrectionDialog: false,
      showErrorDialog: false,
      correctionFormValid: false,
      
      // Filtros
      filters: {
        status: null,
        memberId: '',
        icd10Code: ''
      },
      
      // Paginação
      pagination: {
        currentPage: 1,
        limit: 50,
        totalRecords: 0
      },
      
      // Opções de status
      statusOptions: [
        { text: 'Pending', value: 'pending' },
        { text: 'Submitted', value: 'submitted' },
        { text: 'Error', value: 'error' },
        { text: 'Corrected', value: 'corrected' }
      ],
      
      // Headers da tabela
      headers: [
        { text: 'Member ID', value: 'memberId', sortable: true },
        { text: 'ICD-10 Code', value: 'icd10Code', sortable: true },
        { text: 'Date of Birth', value: 'patientDOB', sortable: true },
        { text: 'Occurrence Date', value: 'occurrenceDateTime', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Error Code', value: 'errorCode', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, width: '100px' }
      ],
      
      // Dados de correção
      correctionData: {
        memberId: '',
        medicareNumber: '',
        patientControlNo: '',
        patientDOB: '',
        icd10Code: '',
        occurrenceDateTime: ''
      },
      
      // Detalhes do erro
      errorDetails: {
        errorCode: '',
        errorMessage: '',
        errorDetails: ''
      },
      
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 5000
      }
    };
  },
  
  mounted() {
    this.loadFileInfo();
    this.loadRecords();
  },
  
  methods: {
    async loadFileInfo() {
      try {
        const fileId = this.$route.params.id;
        const response = await axios.get(`/api/raps/${fileId}`);
        this.fileInfo = response.data;
      } catch (error) {
        console.error('Error loading file information:', error);
        this.showSnackbar('Error loading file information', 'error');
      }
    },
    
    async loadRecords() {
      this.loading = true;
      try {
        const fileId = this.$route.params.id;
        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.limit,
          ...this.filters
        };
        
        // Remove filtros vazios
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null) {
            delete params[key];
          }
        });
        
        const response = await axios.get(`/api/raps/${fileId}/records`, { params });
        this.records = response.data.records;
        this.pagination.totalRecords = response.data.totalRecords;
      } catch (error) {
        console.error('Error loading records:', error);
        this.showSnackbar('Error loading records', 'error');
      } finally {
        this.loading = false;
      }
    },
    
    onPageChange(page) {
      this.pagination.currentPage = page;
      this.loadRecords();
    },
    
    viewRecordDetails(record) {
      this.selectedRecord = record;
      this.showRecordDetailsDialog = true;
    },
    
    correctRecord(record) {
      this.selectedRecord = record;
      this.correctionData = {
        memberId: record.memberId || '',
        medicareNumber: record.medicareNumber || '',
        patientControlNo: record.patientControlNo || '',
        patientDOB: record.patientDOB ? this.formatDateForInput(record.patientDOB) : '',
        icd10Code: record.icd10Code || '',
        occurrenceDateTime: record.occurrenceDateTime ? this.formatDateForInput(record.occurrenceDateTime) : ''
      };
      this.showCorrectionDialog = true;
    },
    
    async saveCorrection() {
      this.correcting = true;
      try {
        const fileId = this.$route.params.id;
        const recordId = this.selectedRecord._id;
        
        await axios.put(`/api/raps/${fileId}/records/${recordId}/correct`, {
          correctedData: this.correctionData
        });
        
        this.showSnackbar('Record corrected successfully!', 'success');
        this.closeCorrectionDialog();
        this.loadRecords();
      } catch (error) {
        console.error('Error correcting record:', error);
        this.showSnackbar('Error correcting record', 'error');
      } finally {
        this.correcting = false;
      }
    },
    
    showErrorDetails(record) {
      this.errorDetails = {
        errorCode: record.errorCode || '',
        errorMessage: record.errorMessage || '',
        errorDetails: record.errorDetails || ''
      };
      this.showErrorDialog = true;
    },
    
    closeCorrectionDialog() {
      this.showCorrectionDialog = false;
      this.selectedRecord = null;
      this.correctionData = {
        memberId: '',
        medicareNumber: '',
        patientControlNo: '',
        patientDOB: '',
        icd10Code: '',
        occurrenceDateTime: ''
      };
      if (this.$refs.correctionForm) {
        this.$refs.correctionForm.reset();
      }
    },
    
    clearFilters() {
      this.filters = {
        status: null,
        memberId: '',
        icd10Code: ''
      };
      this.pagination.currentPage = 1;
      this.loadRecords();
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.currentPage = 1;
        this.loadRecords();
      }, 500);
    },
    
    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        submitted: 'green',
        error: 'red',
        corrected: 'blue'
      };
      return colors[status] || 'grey';
    },
    
    getStatusText(status) {
      const texts = {
        pending: 'Pending',
        submitted: 'Submitted',
        error: 'Error',
        corrected: 'Corrected'
      };
      return texts[status] || 'Unknown';
    },
    
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('pt-BR');
    },
    
    formatDateForInput(date) {
      if (!date) return '';
      return new Date(date).toISOString().split('T')[0];
    },
    
    showSnackbar(message, color = 'success') {
      this.snackbar = {
        show: true,
        message,
        color,
        timeout: 5000
      };
    }
  }
};
</script>

<style scoped>
.v-card {
  margin-bottom: 16px;
}

.v-data-table {
  border-radius: 4px;
}

.text-h4 {
  font-size: 2.125rem !important;
  font-weight: 400;
  line-height: 2.5rem;
  letter-spacing: 0.0073529412em !important;
}

.text-h6 {
  font-size: 1.25rem !important;
  font-weight: 500;
  line-height: 2rem;
  letter-spacing: 0.0125em !important;
}
</style>
